import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/entities/session/auth-store';
import type { CamerasStackParamList } from '@/app/routes';
import { fetchDevices } from '@/entities/device/api';
import { fetchSourceConfig, fetchStreamCameras } from '@/entities/stream/api';
import {
  groupByRecorder,
  type RecorderGroup,
} from '@/entities/stream/recorders';
import type { SourceConfig } from '@/entities/stream/types';
import { CameraCommandsPanel } from '@/features/manage-camera/ui/CameraCommandsPanel';
import { CameraDepartmentPanel } from '@/features/manage-camera/ui/CameraDepartmentPanel';
import { ChannelDepartmentsPanel } from '@/features/manage-camera/ui/ChannelDepartmentsPanel';
import { CameraSecretPanel } from '@/features/manage-camera/ui/CameraSecretPanel';
import { DeleteCameraPanel } from '@/features/manage-camera/ui/DeleteCameraPanel';
import { RemoveRecorderPanel } from '@/features/manage-camera/ui/RemoveRecorderPanel';
import { CameraSettingsForm } from '@/features/manage-camera/ui/CameraSettingsForm';
import { formatTimestamp } from '@/shared/lib/format';
import { Banner } from '@/shared/ui/banner';
import { EmptyState } from '@/shared/ui/empty-state';
import { Page } from '@/shared/ui/page';
import { ListRow, RowGroup } from '@/shared/ui/list-row';
import { Section } from '@/shared/ui/section';
import { StatusPill } from '@/shared/ui/status';
import { Skeleton } from '@/shared/ui/skeleton';
import { radii, spacing } from '@/shared/ui/theme';

/**
 * «Устройства» → камера → настройки и команды.
 *
 * `sourceId` is the camera's SOURCE (the catalogue's `peer`): settings belong to the
 * physical box, which for our own camera is the camera itself.
 *
 * The status on top answers the one question after pressing Save — did the camera
 * take it. While a change is pending the record is re-read every two seconds; an
 * online camera applies within about a second, so a pending that outlives the first
 * few polls is an offline camera, and the banner says the change will apply when it
 * comes back rather than spinning.
 *
 * A video recorder (NVR) gets the same screen with its own body: what it is (number,
 * how many cameras, online), its commands, and removal by RELEASE. The v1 settings form
 * describes one camera's image and the secret panel belongs to cameras added by hand —
 * neither means anything for a box with eight cameras behind it, whose cameras are set
 * up on the recorder itself. Recorder-ness is read from the device row
 * (`device_type: 'nvr'`), not from a route param, so a reloaded page still knows.
 */
type Props = NativeStackScreenProps<CamerasStackParamList, 'CameraSettings'>;

const styles = StyleSheet.create({
  loading: { gap: spacing(4) },
});

function StatusBanner({ config }: { config: SourceConfig }) {
  const { t } = useTranslation();
  if (config.status === 'failed') {
    return (
      <Banner
        tone="error"
        title={t('camera.status.failed')}
        message={config.applyError ?? undefined}
      />
    );
  }
  if (config.status === 'pending') {
    return (
      <Banner
        tone="warning"
        title={t('camera.status.pending')}
        message={t('camera.status.pendingHint')}
      />
    );
  }
  return (
    <Banner
      tone="success"
      title={t('camera.status.applied')}
      message={
        config.reportedAt
          ? t('camera.status.reportedAt', {
              value: formatTimestamp(config.reportedAt),
            })
          : t('camera.status.neverReported')
      }
    />
  );
}

function RecorderInfo({ recorder }: { recorder: RecorderGroup }) {
  const { t } = useTranslation();
  return (
    <RowGroup testID="recorder-info">
      {recorder.machineNumber ? (
        <ListRow
          icon="recorder"
          title={t('camera.recorder.number')}
          value={recorder.machineNumber}
        />
      ) : null}
      <ListRow
        icon="camera"
        title={t('camera.recorder.channels')}
        value={String(recorder.channels.length)}
      />
      <ListRow
        icon="link"
        title={t('camera.recorder.online')}
        trailing={
          <StatusPill
            tone={recorder.online ? 'online' : 'offline'}
            size="sm"
            label={t(recorder.online ? 'live.online' : 'live.notConnected')}
          />
        }
      />
    </RowGroup>
  );
}

export function CameraSettingsScreen({ route, navigation }: Props) {
  const { t } = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const { sourceId, name } = route.params;

  const query = useQuery({
    queryKey: ['source-config', companyId, sourceId],
    queryFn: () => fetchSourceConfig(companyId as string, sourceId),
    enabled: !!companyId,
    refetchInterval: q => (q.state.data?.status === 'pending' ? 2000 : false),
  });

  // Same keys as the camera list: both are usually already in the cache.
  const devices = useQuery({
    queryKey: ['devices', 'camera-sources', companyId],
    queryFn: () => fetchDevices(companyId as string, { perPage: 100 }),
    enabled: !!companyId,
  });
  const cameras = useQuery({
    queryKey: ['stream-cameras', companyId],
    queryFn: () => fetchStreamCameras(companyId as string),
    enabled: !!companyId,
  });
  const recorder =
    groupByRecorder(
      cameras.data?.data ?? [],
      devices.data?.data ?? [],
    ).recorders.find(r => r.peer === sourceId) ?? null;
  const row = (devices.data?.data ?? []).find(d => d.device_id === sourceId);
  // Отдел — у строки устройства; камеры, ни разу не заведённой строкой, тут нет.
  const department = row ? (
    <Section
      title={t('deviceAdmin.department')}
      hint={t(recorder ? 'live.view.recorderDepartmentHint' : 'live.view.cameraDepartmentHint')}
    >
      <CameraDepartmentPanel
        companyId={companyId as string}
        sourceId={sourceId}
        departmentId={row.department_id}
      />
    </Section>
  ) : null;

  let body: React.ReactNode;
  if (!companyId || query.isPending || devices.isPending) {
    body = (
      <View style={styles.loading}>
        <Skeleton height={70} radius={radii.xl} />
        <Skeleton height={220} radius={radii.xl} />
      </View>
    );
  } else if (query.isError) {
    body = (
      <EmptyState
        tone="error"
        title={t('camera.loadError')}
        actionLabel={t('actions.retry')}
        onAction={() => void query.refetch()}
      />
    );
  } else if (recorder) {
    body = (
      <>
        <Section
          title={t('camera.recorder.infoTitle')}
          hint={t('camera.recorder.camerasHint')}
        >
          <RecorderInfo recorder={recorder} />
        </Section>
        {department}
        {recorder.channels.length > 0 ? (
          <Section
            title={t('live.view.channelDepartments')}
            hint={t('live.view.channelDepartmentsHint')}
          >
            <ChannelDepartmentsPanel
              companyId={companyId}
              channels={recorder.channels}
              rows={devices.data?.data ?? []}
              recorder={row}
            />
          </Section>
        ) : null}
        <Section
          title={t('camera.commandsTitle')}
          hint={t('camera.recorder.commandsHint')}
        >
          <CameraCommandsPanel companyId={companyId} sourceId={sourceId} />
        </Section>
        <Section title={t('camera.recorder.remove.title')}>
          <RemoveRecorderPanel
            companyId={companyId}
            sourceId={sourceId}
            machineNumber={recorder.machineNumber}
            name={name}
            onRemoved={() => navigation.popTo('CameraWall')}
          />
        </Section>
      </>
    );
  } else {
    const config = query.data;
    body = (
      <>
        <StatusBanner config={config} />
        {department}
        <Section
          title={t('camera.commandsTitle')}
          hint={t('camera.commandsHint')}
        >
          <CameraCommandsPanel companyId={companyId} sourceId={sourceId} />
        </Section>
        <Section
          title={t('camera.secret.title')}
          hint={t('camera.secret.hint')}
        >
          <CameraSecretPanel companyId={companyId} sourceId={sourceId} />
        </Section>
        <Section title={t('camera.settingsTitle')}>
          {/* Keyed by version: a save or another operator's change reseeds the draft
              from the server instead of keeping stale edits over new values. */}
          <CameraSettingsForm
            key={`${config.desiredVersion}:${config.appliedVersion}`}
            companyId={companyId}
            config={config}
          />
        </Section>
        {/* Last, on purpose: you scroll past everything else to reach it. */}
        <Section title={t('camera.delete.title')}>
          <DeleteCameraPanel
            companyId={companyId}
            sourceId={sourceId}
            name={name}
            onDeleted={() => navigation.popTo('CameraWall')}
          />
        </Section>
      </>
    );
  }

  return (
    <Page
      scroll
      width="form"
      title={name}
      subtitle={t(
        recorder ? 'camera.recorder.subtitle' : 'camera.screenSubtitle',
      )}
      testID="camera-settings"
    >
      {body}
    </Page>
  );
}
